import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import type { NonEmptyString } from '@ipfabric/utils';
import type { BgpCommunities } from '@ipfabric/utils/settings/types/types';
import {
  bgpFileHeaders,
  isBgpProtocol,
  isNativeVrf,
} from 'components/BgpSettings/dataTransfer/bgpDataTransferTypes';
import { splitCellList } from 'components/DataTransferForm/helpers/csvLineParser';
import type { CSVParseConfig } from 'components/DataTransferForm/types';
import { generateGuid } from 'utils/guid';

// Maximum file size for CSV import: 10MB
export const MAX_CSV_FILE_SIZE = 10 * 1024 * 1024;

export const createBgpCsvConfig = (): CSVParseConfig<BgpCommunities> => ({
  headers: bgpFileHeaders,
  maxFileSizeBytes: MAX_CSV_FILE_SIZE,
  rules: [
    {
      field: 'Serial number',
      required: true,
      validator: (value: string) => {
        if (!value.trim()) {
          return 'Serial number is required';
        }
        return null;
      },
    },
    {
      field: 'Protocol',
      required: true,
      validator: (value: string) => {
        if (!isBgpProtocol(value)) {
          return 'Invalid protocol value. Must be IPv4 or IPv6';
        }
        return null;
      },
    },
    {
      field: 'VRF name',
      required: false,
      transformer: (value: string) => value || null,
    },
    {
      field: 'Use native VRF name',
      required: true,
      validator: (value: string) => {
        if (!value) {
          return 'Use native VRF name is required';
        }
        if (!isNativeVrf(value.toLowerCase())) {
          return 'Invalid use native VRF name value. Must be "yes" or "no"';
        }
        return null;
      },
      transformer: (value: string) => value.toLowerCase() === 'yes',
    },
    {
      field: 'List of communities',
      required: true,
      validator: (value: string) => {
        if (!value.trim()) {
          return 'List of communities is required';
        }
        return null;
      },
      transformer: (value, _row, csvDelimiter) =>
        splitCellList(value, csvDelimiter),
    },
  ],
  rowTransformer: (validatedRow): BgpCommunities => {
    const serialNumber = validatedRow['Serial number'];
    const protocol = validatedRow.Protocol;
    const vrfName = validatedRow['VRF name'];
    const useNativeVrf = validatedRow['Use native VRF name'];
    const communities = validatedRow['List of communities'];

    if (!isBgpProtocol(protocol)) {
      throw new IpfError(
        'INVALID_STATE',
        'rowTransformer was called on row with protocol that could not pass valitator',
      );
    }

    return {
      // Temporary generated 'id' is used on the UI, as it is necessary for operations such as deleting an entry in the table. The backend ignores it and assigns its own 'id' afterward.
      id: generateGuid() as NonEmptyString,
      sn: typeof serialNumber === 'string' ? serialNumber : '',
      protocol,
      vrf: typeof vrfName === 'string' ? vrfName : null,
      isVrfStd: typeof useNativeVrf === 'boolean' ? useNativeVrf : false,
      communityList: Array.isArray(communities) ? communities : [],
    };
  },
});
